import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FreelancerAutocomplete } from "@/components/freelancers/FreelancerAutocomplete";
import { invalidDateError } from "@/utils/dateUtils";
import { parseReaisToCents } from "@/utils/money";
import type { NovoPeriodoBody } from "@/types/finance";

interface NewPeriodDialogProps {
  /** Da moeda da proposta. */
  simbolo?: string;
  open: boolean;
  agencyName: string;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (body: NovoPeriodoBody) => Promise<void>;
}

interface NewPeriodDraft {
  freelancerId: string;
  freelancerName?: string;
  start: string;
  end: string;
  value: string;
  role: string;
}

const EMPTY_DRAFT: NewPeriodDraft = { freelancerId: "", start: "", end: "", value: "", role: "" };

/** A period with no project yet: the API creates a draft project for the agency behind it. */
export function NewPeriodDialog({ open, agencyName, saving, simbolo = "R$", onOpenChange, onConfirm }: NewPeriodDialogProps) {
  const [draft, setDraft] = useState<NewPeriodDraft>(EMPTY_DRAFT);

  useEffect(() => {
    if (open) setDraft(EMPTY_DRAFT);
  }, [open]);

  const valueCents = parseReaisToCents(draft.value);
  const endBeforeStart = !!draft.start && !!draft.end && draft.end < draft.start;
  const dateError = invalidDateError(draft.start) ?? invalidDateError(draft.end);
  const ready = !!draft.freelancerId && !!draft.start && !!draft.end && !endBeforeStart && !dateError && !!valueCents && valueCents > 0;

  const confirm = async () => {
    if (!ready || valueCents == null) return;
    await onConfirm({
      freelancer_id: draft.freelancerId,
      data_inicio: draft.start,
      data_conclusao: draft.end,
      valor_profissional_cents: valueCents,
      cargo: draft.role.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo período</DialogTitle>
          <DialogDescription>
            Para um período que ainda não existe em Projetos. Ele é criado em rascunho para {agencyName} e entra nesta
            proposta.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-medium text-muted-foreground">Profissional</Label>
            <FreelancerAutocomplete
              value={draft.freelancerId}
              selectedLabel={draft.freelancerName}
              placeholder="Buscar profissional"
              onValueChange={(freelancerId, freelancer) =>
                setDraft((current) => ({ ...current, freelancerId, freelancerName: freelancer?.nome }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-periodo-inicio" className="text-xs font-medium text-muted-foreground">
              Início
            </Label>
            <Input
              id="novo-periodo-inicio"
              type="date"
              value={draft.start}
              onChange={(event) => setDraft((current) => ({ ...current, start: event.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-periodo-fim" className="text-xs font-medium text-muted-foreground">
              Fim
            </Label>
            <Input
              id="novo-periodo-fim"
              type="date"
              value={draft.end}
              onChange={(event) => setDraft((current) => ({ ...current, end: event.target.value }))}
            />
            {dateError ? (
              <p className="text-[11px] text-destructive">{dateError}</p>
            ) : (
              endBeforeStart && <p className="text-[11px] text-destructive">O fim é anterior ao início.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-periodo-valor" className="text-xs font-medium text-muted-foreground">
              Valor do profissional
            </Label>
            <div className="relative">
              <Input
                id="novo-periodo-valor"
                inputMode="decimal"
                placeholder="0,00"
                value={draft.value}
                onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))}
                className={simbolo === "R$" ? "pr-9" : "pr-11"}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                {simbolo}
              </span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="novo-periodo-cargo" className="text-xs font-medium text-muted-foreground">
              Função / cargo (opcional)
            </Label>
            <Input
              id="novo-periodo-cargo"
              value={draft.role}
              maxLength={200}
              onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" disabled={!ready || saving} onClick={confirm} className="gap-1.5">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Criar período
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
